<template>
  <div class="form-container">
    <div :class="['form']">
      <ValidationObserver ref="form" class="login-form" tag="form" @submit.prevent="login">
        <TextFieldComponent
          class="input-container"
          validationRules="required|email"
          :prependIcon="icons.mail"
          :placeholder="text.email"
          id="email"
          v-model.trim="credentials.email"
        />
        <TextFieldComponent
          class="input-container"
          validationRules="required"
          id="password"
          :prependIcon="icons.lock"
          :appendIcon="icons.visible"
          :placeholder="text.password"
          type="password"
          v-model.trim="credentials.password"
        />
        <StaticAlertComponent v-if="authStore.error" :alert="alert" class="static-alert"></StaticAlertComponent>
        <RememberCheckboxComponent />
        <button type="submit" name="submit" class="login-button">Acceder</button>
      </ValidationObserver>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Icons } from '@/icons/icons'

import { useI18n } from 'vue-i18n-composable'
import TextFieldComponent from '@/components/TextField/TextFieldComponent.vue'
import RememberCheckboxComponent from '@/components/forms/loginForm/RememberCheckboxComponent.vue'
import { ValidationObserver } from 'vee-validate'
import { computed, onUnmounted, reactive, ref } from 'vue'
import { Credentials } from '@/store/modules/auth/authTypes'
import { useAuthStore } from '@/stores/auth/useAuthStore'
import useAuthRedirect from '@/composables/useAuthRedirect'
import { AlertsData, AlertsTypes } from '@/store/modules/alerts/alertsTypes'
import StaticAlertComponent from '@/components/Alert/StaticAlertComponent.vue'
import useSpinner from '@/composables/useSpinner'

const icons = {
  mail: Icons.EMAIL,
  lock: Icons.LOCK,
  visible: Icons.VISIBLE
}

// TRANSLATIONS
const { t } = useI18n()
const { showSpinner, hideSpinner } = useSpinner()
const text = {
  password: t('views.login.password') as string,
  email: t('views.login.email') as string
}
const credentials = reactive<Credentials>({
  email: '',
  password: ''
})

const alert = computed<AlertsData>(() => {
  return {
    type: AlertsTypes.ERROR,
    message: t(authStore.error)
  }
})

const form = ref()

const authStore = useAuthStore()
const { redirectByUser } = useAuthRedirect()

const login = async () => {
  const formIsValid = await form.value.validate()
  if (!formIsValid) {
    return
  }
  showSpinner()

  await authStore.login(credentials)

  if (authStore.error) {
    console.error(authStore.error)
    hideSpinner()
  }
  redirectByUser()
}

onUnmounted(() => {
  hideSpinner()
})


</script>

<style lang="scss" scoped>
.form-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: $spacing-lg;
  align-self: stretch;

  .form {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    align-self: stretch;

    form {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      align-self: stretch;

      ::v-deep(.prepend-icon) {
        &::after {
          content: '';
          width: 1px;
          height: 20px;
          background-color: $neutral-grey-400;
          margin-left: 6px;
        }
      }

      .login-button {
        @include poppins-regular-14;
        color: $neutral-white-000;
        display: flex;
        padding: $spacing-xs $spacing-lg;
        align-items: center;
        align-self: flex-end;
        gap: $spacing-xxs;
        border-radius: $cornerRadius-100;
        background: $secondary-pumpkin-200;
        margin-top: $spacing-sm;

        &:hover {
          background-color: $secondary-pumpkin-100;
        }
      }
    }

    &.api-error ::v-deep(.input-field) {
      border-color: $error-color;

      .prepend-icon::after {
        background-color: $error-color;
      }
    }

    .static-alert {
      margin-bottom: $spacing-sm;
    }
  }
}
</style>
